// Sales Rep Management
import { useState, useCallback, useMemo } from "react";
import { UserCircle, Plus, AlertCircle, Target, TrendingUp } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { KPICard } from "@/components/admin/shared/KPICard";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import { useSalesReps, useCreateSalesRep } from "@/hooks/admin/useB2B";
import type { SalesRep, CreateSalesRepInput } from "@/lib/admin-api/b2b";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SENEGAL_REGIONS = [
  "Dakar",
  "Kaolack",
  "Saint-Louis",
  "Fatick",
  "Thiès-Mbour",
  "Diourbel-Touba",
] as const;

const EMPTY_FORM: CreateSalesRepInput = {
  name: "",
  email: "",
  phone: "",
  region: "Dakar",
  monthly_target_fcfa: 0,
  active: true,
};

// ---------------------------------------------------------------------------
// Create form modal
// ---------------------------------------------------------------------------

interface CreateRepModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSave: (input: CreateSalesRepInput) => Promise<void>;
  readonly isSaving: boolean;
}

function CreateRepModal({
  open,
  onClose,
  onSave,
  isSaving,
}: CreateRepModalProps) {
  const [form, setForm] = useState<CreateSalesRepInput>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setForm(EMPTY_FORM);
    setError(null);
    onClose();
  }, [onClose]);

  const update = useCallback(
    <K extends keyof CreateSalesRepInput>(
      key: K,
      value: CreateSalesRepInput[K],
    ) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (!form.name.trim()) {
      setError("Le nom est requis.");
      return;
    }
    if (!form.phone.trim()) {
      setError("Le téléphone est requis.");
      return;
    }
    setError(null);
    try {
      await onSave(form);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue.");
    }
  }, [form, onSave, handleClose]);

  const inputClass =
    "h-10 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100";
  const labelClass = "mb-1.5 block text-sm font-medium text-stone-700";

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title="Nouveau commercial"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving} className="gap-1.5">
            {isSaving && (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            )}
            Créer
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className={labelClass}>Nom complet</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Prénom Nom"
            className={inputClass}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="commercial@cieda.sn"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Téléphone</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              placeholder="+221 7X XXX XX XX"
              className={inputClass}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Région</label>
            <select
              value={form.region}
              onChange={(e) => update("region", e.target.value)}
              className={inputClass}
            >
              {SENEGAL_REGIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Objectif mensuel (FCFA)</label>
            <input
              type="number"
              min={0}
              value={form.monthly_target_fcfa}
              onChange={(e) =>
                update("monthly_target_fcfa", Number(e.target.value))
              }
              className={inputClass}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            id="rep-active"
            type="checkbox"
            checked={form.active}
            onChange={(e) => update("active", e.target.checked)}
            className="h-4 w-4 rounded border-stone-300 text-amber-600 focus:ring-amber-500"
          />
          <label
            htmlFor="rep-active"
            className="text-sm font-medium text-stone-700"
          >
            Commercial actif
          </label>
        </div>
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function Reps() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const { data, isLoading, isError, refetch } = useSalesReps();
  const createRep = useCreateSalesRep();

  const reps = data?.reps ?? [];

  const kpiData = useMemo(() => {
    const total = reps.length;
    const active = reps.filter((r) => r.active).length;
    const targetTotal = reps.reduce((s, r) => s + r.monthly_target_fcfa, 0);
    const actualTotal = reps.reduce((s, r) => s + r.monthly_actual_fcfa, 0);
    return { total, active, targetTotal, actualTotal };
  }, [reps]);

  const handleSave = useCallback(
    async (input: CreateSalesRepInput) => {
      await createRep.mutateAsync(input);
      setIsCreateOpen(false);
    },
    [createRep],
  );

  const columns: readonly ColumnDef<SalesRep>[] = useMemo(
    () => [
      {
        id: "name",
        header: "Nom",
        cell: (r) => (
          <div>
            <p className="font-medium text-stone-900">{r.name}</p>
            <p className="text-xs text-stone-500">{r.email}</p>
          </div>
        ),
      },
      {
        id: "phone",
        header: "Téléphone",
        cell: (r) => (
          <span className="text-sm text-stone-600">{r.phone}</span>
        ),
      },
      {
        id: "region",
        header: "Région",
        cell: (r) => (
          <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs text-stone-700">
            {r.region}
          </span>
        ),
      },
      {
        id: "customer_count",
        header: "Clients",
        cell: (r) => (
          <span className="text-sm text-stone-600">{r.customer_count}</span>
        ),
      },
      {
        id: "monthly_target_fcfa",
        header: "Objectif",
        cell: (r) => (
          <span className="text-sm text-stone-600">
            {r.monthly_target_fcfa.toLocaleString("fr-SN")} FCFA
          </span>
        ),
      },
      {
        id: "monthly_actual_fcfa",
        header: "Réalisé",
        cell: (r) => (
          <span className="text-sm font-medium text-stone-900">
            {r.monthly_actual_fcfa.toLocaleString("fr-SN")} FCFA
          </span>
        ),
      },
      {
        id: "progress",
        header: "Atteinte",
        cell: (r) => {
          const pct =
            r.monthly_target_fcfa > 0
              ? Math.min(
                  100,
                  Math.round(
                    (r.monthly_actual_fcfa / r.monthly_target_fcfa) * 100,
                  ),
                )
              : 0;
          return (
            <div className="flex items-center gap-2">
              <div className="h-2 w-20 overflow-hidden rounded-full bg-stone-100">
                <div
                  className={`h-full rounded-full transition-all ${
                    pct >= 100
                      ? "bg-emerald-500"
                      : pct >= 70
                        ? "bg-amber-500"
                        : "bg-red-400"
                  }`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-xs text-stone-500">{pct}%</span>
            </div>
          );
        },
      },
      {
        id: "active",
        header: "Statut",
        cell: (r) => (
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
              r.active
                ? "bg-emerald-100 text-emerald-700"
                : "bg-stone-100 text-stone-500"
            }`}
          >
            {r.active ? "Actif" : "Inactif"}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <UserCircle className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Commerciaux
              </h1>
              <p className="text-sm text-stone-500">
                {isLoading ? "Chargement…" : `${reps.length} commercial(aux)`}
              </p>
            </div>
          </div>
          <Button onClick={() => setIsCreateOpen(true)} className="gap-1.5">
            <Plus className="h-4 w-4" />
            Nouveau commercial
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KPICard
            label="Total commerciaux"
            value={kpiData.total}
            format="number"
            icon={<UserCircle className="h-4 w-4" />}
          />
          <KPICard
            label="Actifs"
            value={kpiData.active}
            format="number"
            icon={<UserCircle className="h-4 w-4" />}
          />
          <KPICard
            label="Objectif mensuel total"
            value={kpiData.targetTotal}
            format="currency"
            icon={<Target className="h-4 w-4" />}
          />
          <KPICard
            label="Réalisé total"
            value={kpiData.actualTotal}
            format="currency"
            icon={<TrendingUp className="h-4 w-4" />}
          />
        </div>

        {/* Error */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger les commerciaux.</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Réessayer
            </Button>
          </div>
        )}

        {/* Table */}
        {!isError && (
          <>
            {!isLoading && reps.length === 0 ? (
              <EmptyState
                icon={<UserCircle className="h-6 w-6" />}
                title="Aucun commercial"
                description="Ajoutez votre premier commercial B2B."
                action={{
                  label: "Nouveau commercial",
                  onClick: () => setIsCreateOpen(true),
                }}
              />
            ) : (
              <AdminDataTable
                data={reps}
                columns={columns}
                isLoading={isLoading}
              />
            )}
          </>
        )}
      </div>

      <CreateRepModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSave={handleSave}
        isSaving={createRep.isPending}
      />
    </AdminLayout>
  );
}
